import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Select,
  Input,
  message,
  Popconfirm,
  Space,
  Tag,
  Tooltip,
  Row,
  Col,
  Statistic
} from 'antd';
import {
  PlusOutlined,
  LinkOutlined,
  DisconnectOutlined,
  ReloadOutlined
} from '@ant-design/icons';

const { Option } = Select;

const AssetRelationsManagement = () => {
  const [loading, setLoading] = useState(false);
  const [relations, setRelations] = useState([]);
  const [assets, setAssets] = useState([]);
  const [assetTypes, setAssetTypes] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRelation, setEditingRelation] = useState(null);
  const [form] = Form.useForm();
  
  // 使用 useWatch 监听源资产变化
  const sourceAssetId = Form.useWatch('source_asset_id', form);

  useEffect(() => {
    console.log('AssetRelationsManagement 组件加载，开始获取数据');
    fetchRelations();
    fetchAssets();
    fetchAssetTypes();
  }, []);

  // 添加调试用的 useEffect
  useEffect(() => {
    console.log('assets 状态更新:', assets);
  }, [assets]);

  useEffect(() => {
    console.log('assetTypes 状态更新:', assetTypes);
  }, [assetTypes]);

  // 获取关联关系列表
  const fetchRelations = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/assets/asset-relations', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setRelations(Array.isArray(data.data) ? data.data : []);
      } else {
        message.error(data.message || '获取关联关系失败');
        setRelations([]);
      }
    } catch (error) {
      console.error('获取关联关系失败:', error);
      message.error('获取关联关系失败');
      setRelations([]);
    } finally {
      setLoading(false);
    }
  };

  // 获取资产列表
  const fetchAssets = async () => {
    try {
      // 构建查询参数，只包含有效的参数
      const params = new URLSearchParams();
      params.append('page', '1');
      params.append('limit', '100');
      
      const response = await fetch(`/api/assets?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      
      console.log('资产列表API响应:', data);
      
      if (data.success) {
        const assetsList = Array.isArray(data.data.assets) ? data.data.assets : [];
        console.log('解析后的资产列表:', assetsList);
        setAssets(assetsList);
      } else {
        console.error('获取资产列表失败:', data.message);
        message.error(data.message || '获取资产列表失败');
        setAssets([]);
      }
    } catch (error) {
      console.error('获取资产列表失败:', error);
      message.error('获取资产列表失败');
      setAssets([]);
    }
  };

  // 获取资产类型列表
  const fetchAssetTypes = async () => {
    try {
      const response = await fetch('/api/assets/types', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      
      console.log('资产类型API响应:', data);
      
      if (data.success) {
        const typesList = Array.isArray(data.data) ? data.data : [];
        console.log('解析后的资产类型列表:', typesList);
        setAssetTypes(typesList);
      } else {
        console.error('获取资产类型失败:', data.message);
        setAssetTypes([]);
      }
    } catch (error) {
      console.error('获取资产类型失败:', error);
      setAssetTypes([]);
    }
  };

  // 创建/更新关联关系
  const handleSubmit = async (values) => {
    try {
      const url = editingRelation 
        ? `/api/assets/relations/${editingRelation.id}` 
        : '/api/assets/asset-relations';
      
      const method = editingRelation ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(values)
      });
      
      const data = await response.json();
      
      if (data.success) {
        message.success(editingRelation ? '更新关联关系成功' : '创建关联关系成功');
        setModalVisible(false);
        form.resetFields();
        setEditingRelation(null);
        fetchRelations();
      } else {
        message.error(data.message || '操作失败');
      }
    } catch (error) {
      console.error('操作失败:', error);
      message.error('操作失败');
    }
  };

  // 删除关联关系
  const handleDelete = async (id) => {
    try {
      const response = await fetch(`/api/assets/relations/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        message.success('删除关联关系成功');
        fetchRelations();
      } else {
        message.error(data.message || '删除失败');
      }
    } catch (error) {
      console.error('删除失败:', error);
      message.error('删除失败');
    }
  };

  // 打开编辑模态框
  const handleEdit = (record) => {
    setEditingRelation(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  // 获取资产类型名称
  const getAssetTypeName = (typeId) => {
    console.log('获取资产类型名称，typeId:', typeId, 'assetTypes:', assetTypes);
    const type = assetTypes.find(t => t.id === typeId);
    console.log('找到的类型:', type);
    return type ? type.type_name : '未知类型';
  };

  // 获取资产名称
  const getAssetName = (assetId) => {
    const asset = assets.find(a => a.id === assetId);
    return asset ? asset.name : '未知资产';
  };

  // 根据源资产过滤目标资产
  const filterTargetAssets = (sourceAssetId) => {
    if (!sourceAssetId) return assets;
    
    const sourceAsset = assets.find(a => a.id === sourceAssetId);
    if (!sourceAsset) return assets;

    // 根据资产类型关系规则过滤
    // 例如：SIM卡只能安装在手机上，抖音/微信账号只能关联到手机
    const relationRules = {
      sim_card: ['mobile_phone'], // SIM卡 -> 手机
      douyin_account: ['mobile_phone'], // 抖音账号 -> 手机
      wechat_account: ['mobile_phone'], // 微信账号 -> 手机
    };

    const allowedTypes = relationRules[sourceAsset.type_code] || [];
    
    // 如果有特定的关系规则，按规则过滤
    if (allowedTypes.length > 0) {
      const filteredAssets = assets.filter(asset => 
        asset.id !== sourceAssetId && 
        allowedTypes.includes(asset.type_code)
      );
      
      // 如果按规则过滤后没有资产，则显示所有其他资产
      if (filteredAssets.length === 0) {
        return assets.filter(asset => asset.id !== sourceAssetId);
      }
      
      return filteredAssets;
    }
    
    // 如果没有特定规则，显示所有其他资产
    return assets.filter(asset => asset.id !== sourceAssetId);
  };

  // 表格列定义
  const columns = [
    {
      title: '源资产',
      key: 'source_asset',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>
            {getAssetName(record.source_asset_id)}
          </div>
          <Tag color="blue">{getAssetTypeName(record.source_asset_type_id)}</Tag>
        </div>
      ),
    },
    {
      title: '关联关系',
      dataIndex: 'relation_type',
      key: 'relation_type',
      render: (type) => {
        const typeMap = {
          contains: { text: '包含', color: 'cyan' },
          linked_to: { text: '关联到', color: 'blue' },
          installed_in: { text: '安装于', color: 'green' },
          belongs_to: { text: '属于', color: 'purple' },
          // 兼容旧数据
          owned_by: { text: '属于', color: 'purple' },
          managed_by: { text: '管理于', color: 'orange' }
        };
        const config = typeMap[type] || { text: type, color: 'default' };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: '目标资产',
      key: 'target_asset',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>
            {getAssetName(record.target_asset_id)}
          </div>
          <Tag color="blue">{getAssetTypeName(record.target_asset_type_id)}</Tag>
        </div>
      ),
    },
    {
      title: '备注',
      dataIndex: 'notes',
      key: 'notes',
      ellipsis: true,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Tooltip title="编辑">
            <Button 
              type="link" 
              icon={<LinkOutlined />} 
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="确定要删除这个关联关系吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Tooltip title="删除">
              <Button 
                type="link" 
                danger 
                icon={<DisconnectOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // 统计数据
  const statistics = {
    totalRelations: relations.length,
    installedRelations: relations.filter(r => r.relation_type === 'installed_in').length,
    linkedRelations: relations.filter(r => r.relation_type === 'linked_to').length,
    ownedRelations: relations.filter(r => r.relation_type === 'belongs_to' || r.relation_type === 'owned_by').length
  };

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Row gutter={16} style={{ marginBottom: '16px' }}>
          <Col span={6}>
            <Statistic title="关联关系总数" value={statistics.totalRelations} />
          </Col>
          <Col span={6}>
            <Statistic title="安装关系" value={statistics.installedRelations} />
          </Col>
          <Col span={6}>
            <Statistic title="关联关系" value={statistics.linkedRelations} />
          </Col>
          <Col span={6}>
            <Statistic title="归属关系" value={statistics.ownedRelations} />
          </Col>
        </Row>

        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between' }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingRelation(null);
              form.resetFields();
              setModalVisible(true);
            }}
          >
            新建关联关系
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchRelations}
          >
            刷新
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={relations}
          rowKey="id"
          loading={loading}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
          }}
        />
      </Card>

      {/* 关联关系编辑模态框 */}
      <Modal
        title={editingRelation ? '编辑关联关系' : '新建关联关系'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setEditingRelation(null);
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="source_asset_id"
            label="源资产"
            rules={[{ required: true, message: '请选择源资产' }]}
          >
            <Select
              placeholder="请选择源资产"
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {assets.map(asset => (
                <Option key={asset.id} value={asset.id}>
                  {asset.name} ({getAssetTypeName(asset.asset_type_id)})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="relation_type"
            label="关联关系"
            rules={[{ required: true, message: '请选择关联关系' }]}
          >
            <Select placeholder="请选择关联关系">
              <Option value="contains">包含</Option>
              <Option value="linked_to">关联到</Option>
              <Option value="installed_in">安装于</Option>
              <Option value="belongs_to">属于</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="target_asset_id"
            label="目标资产"
            rules={[{ required: true, message: '请选择目标资产' }]}
          >
            <Select
              placeholder="请选择目标资产"
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              dependencies={['source_asset_id']}
            >
              {sourceAssetId && 
                filterTargetAssets(sourceAssetId).map(asset => (
                  <Option key={asset.id} value={asset.id}>
                    {asset.name} ({getAssetTypeName(asset.asset_type_id)})
                  </Option>
                ))
              }
            </Select>
          </Form.Item>

          <Form.Item
            name="notes"
            label="备注"
          >
            <Input.TextArea rows={3} placeholder="请输入备注" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                {editingRelation ? '更新' : '创建'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AssetRelationsManagement;
